import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useAiStore } from '@/store/aiStore';
import { brainstorm } from '@/api/ai';
import type { MindMapNode } from '@/model/types';

type AiMode = 'expand' | 'summarize' | 'question';

function serializeBranchAsText(
  nodeId: string,
  nodes: Map<string, MindMapNode>,
  maxDepth?: number,
  currentDepth: number = 0,
): string {
  const node = nodes.get(nodeId);
  if (!node) return '';
  const indent = '  '.repeat(currentDepth);
  let result = `${indent}${node.text || '(empty)'}\n`;
  if (maxDepth !== undefined && currentDepth >= maxDepth) return result;
  for (const childId of node.childIds) {
    result += serializeBranchAsText(childId, nodes, maxDepth, currentDepth + 1);
  }
  return result;
}

// panelStyle is now computed inside AiPanel to support responsive layout

const modeBtnBase: React.CSSProperties = {
  flex: 1,
  padding: '6px 8px',
  fontSize: 12,
  fontFamily: 'inherit',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

const actionBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  background: 'var(--color-primary-hover)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const disabledBtnStyle: React.CSSProperties = {
  ...actionBtnStyle,
  background: 'var(--bg-surface-raised)',
  color: 'var(--text-tertiary)',
  cursor: 'default',
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  background: 'var(--bg-surface)',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const resultBoxStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  padding: 12,
  color: 'var(--text-primary)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
};

export function AiPanel() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const aiPanelNodeId = useEditorStore((s) => s.aiPanelNodeId);
  const aiPanelMode = useEditorStore((s) => s.aiPanelMode);
  const closeAiPanel = useEditorStore((s) => s.closeAiPanel);
  const openAiPanel = useEditorStore((s) => s.openAiPanel);
  const nodes = useDocumentStore((s) => s.nodes);
  const mapDoc = useDocumentStore((s) => s.mapDoc);
  const credits = useAiStore((s) => s.credits);
  const fetchCredits = useAiStore((s) => s.fetchCredits);

  const [mode, setMode] = useState<AiMode>('expand');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [question, setQuestion] = useState('');

  // Expand mode state
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Summarize / Ask mode state
  const [resultText, setResultText] = useState('');

  const node = aiPanelNodeId ? nodes.get(aiPanelNodeId) : undefined;

  // Sync mode from store when panel opens
  useEffect(() => {
    if (aiPanelMode) {
      setMode(aiPanelMode);
    }
  }, [aiPanelMode]);

  // Reset state when node changes
  useEffect(() => {
    setSuggestions([]);
    setResultText('');
    setError(null);
    setQuestion('');
  }, [aiPanelNodeId]);

  const handleModeChange = useCallback(
    (newMode: AiMode) => {
      setMode(newMode);
      setSuggestions([]);
      setResultText('');
      setError(null);
      if (aiPanelNodeId) {
        openAiPanel(aiPanelNodeId, newMode);
      }
    },
    [aiPanelNodeId, openAiPanel],
  );

  const branchContext = aiPanelNodeId
    ? serializeBranchAsText(aiPanelNodeId, nodes, 3)
    : '';

  const handleGenerate = useCallback(async () => {
    if (!node || !aiPanelNodeId) return;
    setIsLoading(true);
    setError(null);
    setSuggestions([]);
    setResultText('');

    try {
      const response = await brainstorm(
        node.text,
        branchContext,
        mode,
        mode === 'question' ? question : undefined,
      );

      if (mode === 'expand') {
        if (Array.isArray(response.result)) {
          setSuggestions(response.result);
        } else {
          // Fallback: parse newline-separated text
          const items = response.result
            .split('\n')
            .map((line: string) => line.replace(/^[-*\d.)\s]+/, '').trim())
            .filter((line: string) => line.length > 0);
          setSuggestions(items);
        }
      } else {
        setResultText(
          Array.isArray(response.result)
            ? response.result.join('\n')
            : response.result,
        );
      }

      // Refresh credits after use
      void fetchCredits();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [node, aiPanelNodeId, branchContext, mode, question, fetchCredits]);

  const handleAddSuggestion = useCallback(
    (text: string) => {
      if (!aiPanelNodeId) return;
      mapDoc.addChild(aiPanelNodeId, text);
      setSuggestions((prev) => prev.filter((s) => s !== text));
    },
    [aiPanelNodeId, mapDoc],
  );

  const handleAddAll = useCallback(() => {
    if (!aiPanelNodeId) return;
    for (const text of suggestions) {
      mapDoc.addChild(aiPanelNodeId, text);
    }
    setSuggestions([]);
  }, [aiPanelNodeId, mapDoc, suggestions]);

  const handleReplaceNodeText = useCallback(() => {
    if (!aiPanelNodeId || !resultText) return;
    mapDoc.updateNodeText(aiPanelNodeId, resultText);
  }, [aiPanelNodeId, mapDoc, resultText]);

  const handleAddAsNote = useCallback(() => {
    if (!aiPanelNodeId || !resultText) return;
    const currentNotes = node?.notes ?? '';
    const newNotes = currentNotes
      ? `${currentNotes}\n\n${resultText}`
      : resultText;
    mapDoc.updateNodeNotes(aiPanelNodeId, newNotes);
  }, [aiPanelNodeId, mapDoc, resultText, node]);

  const handleAddAsChild = useCallback(() => {
    if (!aiPanelNodeId || !resultText) return;
    mapDoc.addChild(aiPanelNodeId, resultText);
  }, [aiPanelNodeId, mapDoc, resultText]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeAiPanel();
      }
    },
    [closeAiPanel],
  );

  if (!aiPanelNodeId || !node) return null;

  const remaining = credits ? credits.limit - credits.used : null;
  const noCredits = remaining !== null && remaining <= 0 && !credits?.hasByokKey;

  const panelStyle: React.CSSProperties = {
    position: isMobile ? 'fixed' : 'absolute',
    top: isMobile ? 0 : 50,
    bottom: isMobile ? 0 : 28,
    right: 0,
    width: isMobile ? '100%' : 320,
    zIndex: isMobile ? 35 : 20,
    background: 'var(--bg-app)',
    borderLeft: isMobile ? 'none' : '1px solid var(--border-subtle)',
    fontFamily: 'var(--font-family)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'aiPanelSlideIn 150ms ease-out',
  };

  return (
    <>
    {isMobile && (
      <div
        onClick={closeAiPanel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--bg-overlay)',
          zIndex: 34,
        }}
      />
    )}
    <div style={panelStyle} onKeyDown={handleKeyDown} tabIndex={-1}>
      <style>{`
        @keyframes aiPanelSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          paddingTop: isMobile ? 'var(--space-4)' : 12,
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span
            style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('panels.aiAssistant')}
          </span>
          {credits && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>
                {t('panels.credits', { used: credits.used, limit: credits.limit })}
              </span>
              <div
                style={{
                  width: 40,
                  height: 3,
                  background: 'var(--bg-surface-raised)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, (credits.used / credits.limit) * 100)}%`,
                    height: '100%',
                    background: credits.used / credits.limit > 0.8 ? 'var(--color-danger)' : 'var(--color-primary-hover)',
                    transition: 'width 200ms ease',
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <button
          onClick={closeAiPanel}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-lg)',
            padding: '0 4px',
            lineHeight: 1,
          }}
          title={t('panels.closePanel', { panel: 'AI' })}
        >
          x
        </button>
      </div>

      {/* Node title */}
      <div
        style={{
          padding: '8px 16px',
          color: 'var(--text-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {node.text || t('panels.emptyNode')}
      </div>

      {/* Mode selector */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '0 16px 12px',
          flexShrink: 0,
        }}
      >
        {(['expand', 'summarize', 'question'] as const).map((m) => (
          <button
            key={m}
            onClick={() => handleModeChange(m)}
            style={{
              ...modeBtnBase,
              background: mode === m ? 'var(--color-primary-hover)' : 'var(--bg-surface)',
              color: mode === m ? '#fff' : 'var(--text-secondary)',
              borderColor: mode === m ? 'var(--color-primary-hover)' : 'var(--border-default)',
            }}
          >
            {m === 'expand' ? t('panels.expand') : m === 'summarize' ? t('panels.summarize') : t('panels.askQuestion')}
          </button>
        ))}
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 12px' }}>
        {/* No credits warning */}
        {noCredits && (
          <div
            style={{
              background: 'var(--bg-warning)',
              border: '1px solid #78350f',
              borderRadius: 6,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ color: 'var(--text-warning)', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              {t('panels.noCredits')}
            </div>
            <div style={{ color: 'var(--text-warning)', fontSize: 'var(--font-size-xs)', lineHeight: 1.4 }}>
              {t('panels.noCreditsDescription')}
            </div>
            <button
              style={{
                ...secondaryBtnStyle,
                marginTop: 8,
                color: 'var(--text-warning)',
                borderColor: '#78350f',
              }}
            >
              {t('panels.useOwnKey')}
            </button>
          </div>
        )}

        {/* Expand mode */}
        {mode === 'expand' && (
          <div>
            <button
              onClick={() => void handleGenerate()}
              disabled={isLoading || noCredits}
              style={isLoading || noCredits ? disabledBtnStyle : actionBtnStyle}
            >
              {isLoading ? t('panels.thinking') : t('panels.generateIdeas')}
            </button>

            {isLoading && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 8, textAlign: 'center' }}>
                {t('panels.thinking')}
              </div>
            )}

            {suggestions.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>
                    {t('panels.suggestions', { count: suggestions.length })}
                  </span>
                  <button
                    onClick={handleAddAll}
                    style={secondaryBtnStyle}
                  >
                    {t('panels.addAll')}
                  </button>
                </div>
                {suggestions.map((text, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      padding: '8px 10px',
                      background: 'var(--bg-surface)',
                      borderRadius: 'var(--radius-sm)',
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        color: 'var(--text-primary)',
                        fontSize: 12,
                        lineHeight: 1.4,
                      }}
                    >
                      {text}
                    </span>
                    <button
                      onClick={() => handleAddSuggestion(text)}
                      style={{
                        ...secondaryBtnStyle,
                        flexShrink: 0,
                        color: 'var(--text-link)',
                        borderColor: 'var(--color-primary-hover)',
                      }}
                    >
                      {t('panels.add')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summarize mode */}
        {mode === 'summarize' && (
          <div>
            <div
              style={{
                ...resultBoxStyle,
                marginBottom: 8,
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-secondary)',
                maxHeight: 120,
                overflowY: 'auto',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10, textTransform: 'uppercase' }}>
                {t('panels.branchContext')}
              </div>
              {branchContext || t('panels.noChildren')}
            </div>

            <button
              onClick={() => void handleGenerate()}
              disabled={isLoading || noCredits}
              style={isLoading || noCredits ? disabledBtnStyle : actionBtnStyle}
            >
              {isLoading ? t('panels.summarizing') : t('panels.summarize')}
            </button>

            {isLoading && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 8, textAlign: 'center' }}>
                {t('panels.thinking')}
              </div>
            )}

            {resultText && (
              <div style={{ marginTop: 12 }}>
                <div style={resultBoxStyle}>{resultText}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button onClick={handleReplaceNodeText} style={secondaryBtnStyle}>
                    {t('panels.replaceNodeText')}
                  </button>
                  <button onClick={handleAddAsNote} style={secondaryBtnStyle}>
                    {t('panels.addAsNote')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ask mode */}
        {mode === 'question' && (
          <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input
                type="text"
                placeholder={t('panels.askPlaceholder')}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && question.trim() && !isLoading && !noCredits) {
                    void handleGenerate();
                  }
                  e.stopPropagation();
                }}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => void handleGenerate()}
                disabled={isLoading || noCredits || !question.trim()}
                style={
                  isLoading || noCredits || !question.trim()
                    ? disabledBtnStyle
                    : actionBtnStyle
                }
              >
                {isLoading ? '...' : t('panels.ask')}
              </button>
            </div>

            {isLoading && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, textAlign: 'center' }}>
                {t('panels.thinking')}
              </div>
            )}

            {resultText && (
              <div style={{ marginTop: 8 }}>
                <div style={resultBoxStyle}>{resultText}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button onClick={handleAddAsChild} style={secondaryBtnStyle}>
                    {t('panels.addAsChild')}
                  </button>
                  <button onClick={handleAddAsNote} style={secondaryBtnStyle}>
                    {t('panels.addAsNote')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error display */}
        {error && (
          <div
            style={{
              color: 'var(--text-error)',
              fontSize: 'var(--font-size-xs)',
              marginTop: 8,
              padding: '6px 8px',
              background: 'var(--bg-error-subtle)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
